import { useEffect, useRef } from "react";
import { Gift, Heart, Sparkles, Leaf, Brain, BookOpen, Coffee, Hand, Activity, Quote, ExternalLink, ArrowRight, Package } from "lucide-react";

const BROGLIEBOX_IMAGE = "https://customer-assets.emergentagent.com/job_trauma-youth-digital/artifacts/sbvhcm8u_1.jpg";

const BOX_CATEGORIES = [
  {
    icon: Leaf,
    title: "Aromatherapy & Sleep",
    desc: "Aromatherapy products, candles, herbal teas, and sleep aids to help caregivers wind down and rest.",
    color: "#3DAA35",
  },
  {
    icon: Brain,
    title: "Mindfulness Tools",
    desc: "Gratitude journals, affirmation clings, and guided reflection resources for daily grounding.",
    color: "#0E8DB5",
  },
  {
    icon: Hand,
    title: "Stress Relief & Sensory",
    desc: "Fidget tools, grounding kits, and sensory items designed to ease tension in difficult moments.",
    color: "#1A3A4A",
  },
  {
    icon: Activity,
    title: "Lifestyle & Wellness",
    desc: "Planning pads, light fitness items, and wellness snacks that support everyday caregiver routines.",
    color: "#3DAA35",
  },
  {
    icon: BookOpen,
    title: "Educational Materials",
    desc: "Thoughtfully chosen resources that build understanding and offer evidence-based mental health insights.",
    color: "#0E8DB5",
  },
  {
    icon: Heart,
    title: "Inspirational Keepsakes",
    desc: "Comforting tokens designed to remind caregivers they are seen, valued, and never alone in this journey.",
    color: "#1A3A4A",
  },
];

function useScrollReveal(ref) {
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => entries.forEach((entry) => { if (entry.isIntersecting) entry.target.querySelectorAll(".animate-on-scroll, .slide-left, .slide-right, .scale-in, .blur-in, .stagger-item, .line-animate").forEach((el) => el.classList.add("visible")); }),
      { threshold: 0.05 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [ref]);
}

export default function CaregiverBoxes() {
  const s1 = useRef(null);
  const s2 = useRef(null);
  const s3 = useRef(null);
  const s4 = useRef(null);
  useScrollReveal(s1);
  useScrollReveal(s2);
  useScrollReveal(s3);
  useScrollReveal(s4);

  return (
    <main className="pt-24">
      {/* Hero */}
      <section ref={s1} className="py-20 md:py-28 bg-gradient-to-b from-[#1A3A4A] to-[#1A3A4A]/95 text-white relative overflow-hidden">
        <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-[#3DAA35]/15 rounded-full translate-x-1/3 -translate-y-1/3 blur-3xl" />
        <div className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-[#0E8DB5]/15 rounded-full -translate-x-1/3 translate-y-1/3 blur-3xl" />

        <div className="max-w-7xl mx-auto px-6 md:px-12 relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="animate-on-scroll">
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 border border-white/20 backdrop-blur-sm mb-6">
                <Sparkles className="w-4 h-4 text-[#7CC8E0]" />
                <span className="text-xs font-sans font-semibold tracking-wider uppercase text-white/90">Caregiver Self-Care Initiative</span>
              </div>

              <p className="text-xs font-sans tracking-[0.25em] uppercase font-semibold text-[#7CC8E0] mb-4">
                <span className="inline-block w-10 h-[2px] bg-[#3DAA35] mr-3 align-middle" />For The Caregivers
              </p>

              <h1 data-testid="caregiver-boxes-heading" className="text-4xl sm:text-5xl lg:text-6xl font-serif font-bold tracking-tight leading-tight mb-6">
                Caregiver <span className="text-[#7CC8E0]">Boxes</span>
              </h1>

              <p className="text-base sm:text-lg font-sans font-medium text-white/85 leading-relaxed mb-6">
                Caring for a youth in crisis takes extraordinary strength — and the people who give that care deserve care in return. TYSS partners with BroglieBox to deliver thoughtfully curated self-care boxes directly to caregivers of youth navigating mental health challenges.
              </p>

              <div className="flex flex-wrap gap-3">
                <a href="https://brogliebox.com/" target="_blank" rel="noopener noreferrer"
                  data-testid="caregiver-boxes-visit-brogliebox" className="btn-pill btn-primary no-underline text-sm">
                  Visit BroglieBox <ExternalLink className="w-4 h-4" />
                </a>
                <a href="#whats-inside" className="btn-pill btn-secondary no-underline text-sm">
                  See What's Inside <ArrowRight className="w-4 h-4" />
                </a>
              </div>
            </div>

            <div className="animate-on-scroll delay-200 relative">
              <div className="absolute -inset-4 bg-gradient-to-br from-[#3DAA35]/30 to-[#0E8DB5]/30 rounded-3xl blur-2xl" />
              <img
                src={BROGLIEBOX_IMAGE}
                alt="BroglieBox self-care package featuring wellness products, journals, aromatherapy items, and inspirational keepsakes"
                className="relative w-full h-auto rounded-3xl shadow-2xl object-cover"
                style={{ maxHeight: "560px" }}
              />
            </div>
          </div>
        </div>
      </section>

      {/* Partnership story */}
      <section ref={s2} className="py-20 md:py-24 bg-white relative overflow-hidden">
        <div className="absolute top-20 right-0 w-[300px] h-[300px] bg-[#3DAA35]/5 rounded-full translate-x-1/2 blur-3xl" />
        <div className="max-w-5xl mx-auto px-6 md:px-12 relative z-10">
          <div className="text-center mb-12 animate-on-scroll">
            <p className="text-xs font-sans tracking-[0.25em] uppercase font-semibold text-earth-muted mb-4">
              <span className="inline-block w-10 h-[2px] bg-[#3DAA35] mr-3 align-middle" />Our Partnership<span className="inline-block w-10 h-[2px] bg-[#3DAA35] ml-3 align-middle" />
            </p>
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-serif font-bold tracking-tight text-earth mb-4">
              TYSS &amp; <span className="gradient-text">BroglieBox</span>
            </h2>
          </div>

          <div className="animate-on-scroll delay-100 bg-[#F8FAFB] border border-[#C8DDD8]/60 rounded-3xl p-8 md:p-12 shadow-sm">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 rounded-2xl bg-[#3DAA35]/10 flex items-center justify-center flex-shrink-0">
                <Gift className="w-6 h-6 text-[#3DAA35]" />
              </div>
              <div>
                <p className="text-xs font-sans font-semibold tracking-wider uppercase text-earth-muted mb-0.5">Empowering Caregivers</p>
                <p className="text-sm font-sans font-medium text-earth">A shared mission of mental health advocacy</p>
              </div>
            </div>

            <div className="space-y-5 text-base sm:text-lg font-sans leading-relaxed text-earth-muted">
              <p>
                TYSS, Inc. partners with <strong className="text-earth">BroglieBox</strong>, an organization founded by dedicated mental health advocates inspired by their own lived experiences navigating mental health challenges.
              </p>
              <p>
                Each box is thoughtfully designed to empower caregivers in their own mental health journey — helping them feel less alone while reminding them that support and resources are always within reach.
              </p>
            </div>

            <div className="mt-8 pt-8 border-t border-[#C8DDD8]/60 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div className="flex items-center gap-3">
                <Quote className="w-5 h-5 text-[#0E8DB5]/60" />
                <p className="text-sm font-serif italic text-earth">"Those who give the most care also deserve to receive it."</p>
              </div>
              <a href="https://brogliebox.com/" target="_blank" rel="noopener noreferrer"
                className="text-sm font-sans font-semibold text-[#0E8DB5] hover:underline inline-flex items-center gap-1 no-underline">
                brogliebox.com <ExternalLink className="w-3.5 h-3.5" />
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* What's Inside */}
      <section id="whats-inside" ref={s3} className="py-20 md:py-24 bg-[#F8FAFB] relative overflow-hidden">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[600px] h-[600px] bg-[#0E8DB5]/5 rounded-full blur-3xl" />

        <div className="max-w-7xl mx-auto px-6 md:px-12 relative z-10">
          <div className="text-center mb-14 animate-on-scroll">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#0E8DB5]/10 border border-[#0E8DB5]/20 mb-6">
              <Package className="w-4 h-4 text-[#0E8DB5]" />
              <span className="text-xs font-sans font-semibold tracking-wider uppercase text-[#0E8DB5]">What's Inside</span>
            </div>
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-serif font-bold tracking-tight text-earth mb-4">
              A Thoughtful <span className="gradient-text">Curation</span>
            </h2>
            <p className="text-base sm:text-lg font-sans text-earth-muted max-w-2xl mx-auto leading-relaxed">
              Each specially curated BroglieBox includes a thoughtful mix of self-care and wellness products designed to support emotional and mental well-being.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {BOX_CATEGORIES.map((cat, i) => (
              <div
                key={cat.title}
                data-testid={`box-category-${i}`}
                className={`animate-on-scroll delay-${(i % 3 + 1) * 100} bento-card rounded-2xl bg-white border border-[#C8DDD8]/50 p-7 hover:-translate-y-1 transition-all duration-300`}
              >
                <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-4" style={{ backgroundColor: `${cat.color}15` }}>
                  <cat.icon className="w-6 h-6" style={{ color: cat.color }} />
                </div>
                <h3 className="text-lg font-serif font-bold text-earth mb-2">{cat.title}</h3>
                <p className="text-sm font-sans text-earth-muted leading-relaxed">{cat.desc}</p>
              </div>
            ))}
          </div>

          <div className="mt-12 animate-on-scroll delay-300 max-w-3xl mx-auto text-center bg-white rounded-3xl border border-[#C8DDD8]/60 p-8 md:p-10">
            <Coffee className="w-8 h-8 text-[#3DAA35] mx-auto mb-4" />
            <p className="text-base sm:text-lg font-sans text-earth-muted leading-relaxed italic">
              Every box is built to promote comfort, resilience, and a sense of connection — so caregivers know they are not alone in this work.
            </p>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section ref={s4} className="py-20 md:py-24 bg-gradient-to-br from-[#1A3A4A] via-[#1A3A4A]/95 to-[#0E8DB5]/40 text-white relative overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_50%,rgba(124,200,224,0.15),transparent_60%)]" />
        <div className="max-w-4xl mx-auto px-6 md:px-12 relative z-10 text-center">
          <div className="animate-on-scroll">
            <Gift className="w-12 h-12 text-[#7CC8E0] mx-auto mb-6" />
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-serif font-bold tracking-tight leading-tight mb-6">
              Send Care to a <span className="text-[#7CC8E0]">Caregiver</span>
            </h2>
            <p className="text-base sm:text-lg font-sans text-white/85 leading-relaxed max-w-2xl mx-auto mb-8">
              Help us put a BroglieBox in the hands of a caregiver who needs encouragement most. Every contribution makes care possible.
            </p>
            <div className="flex flex-wrap gap-4 justify-center">
              <a href="https://brogliebox.com/" target="_blank" rel="noopener noreferrer"
                data-testid="caregiver-boxes-cta-visit" className="btn-pill btn-primary no-underline text-sm">
                Visit BroglieBox <ExternalLink className="w-4 h-4" />
              </a>
              <a href="https://20d0a0a2-9bc3-4878-a451-dbaa4c9318b7.paylinks.godaddy.com/bfd32eeb-318a-425f-b9fb-26a" target="_blank" rel="noopener noreferrer"
                data-testid="caregiver-boxes-cta-support" className="btn-pill btn-secondary border-white/30 text-white hover:bg-white/10 no-underline text-sm">
                Support TYSS <Heart className="w-4 h-4" />
              </a>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
}
